import React, { useState, useEffect, useContext, createContext } from "react";
import * as firebase from "firebase/app";
import "firebase/auth";

import { parseDocSnapshot, getFile } from "hooks/utils";
import { userGlickoHistoryFromTrials } from "utils/glicko";

const authContext = createContext();

// Provider component that wraps your app and makes auth object
// available to any child component that calls useAuth().
export const ProvideAuth = ({ children }) => (
  <authContext.Provider value={useProvideAuth()}>
    {children}
  </authContext.Provider>
);

// Hook for child components to get the auth object
// and re-render when it changes.
export const useAuth = () => useContext(authContext);
// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState();
  const [trials, setTrials] = useState();
  const [exercises, setExercises] = useState();
  const [profile, setProfile] = useState();

  const [glickoHistory, setGlickoHistory] = useState();
  const [error, setError] = useState();

  // Subscribe to user on mount
  // Because this sets state in the callback it will cause any
  // component that utilizes this hook to re-render with the
  // latest auth object.
  useEffect(() => {
    let unsubscribeTrials;
    let unsubscribeExercises;
    let unsubscribeProfile;
    const unsubscribe = firebase.auth().onAuthStateChanged(user => {
      if (user) {
        window.analytics.identify(user.uid, {
          email: user.email
        });
        setUser(user);
        unsubscribeTrials = firebase
          .firestore()
          .collection("trials")
          .where("userId", "==", user.uid)
          .onSnapshot(snapshot => {
            const _trials = parseAll(snapshot);
            _trials.sort((a, b) => getMillis(a) - getMillis(b));
            setTrials(_trials);
            setGlickoHistory(userGlickoHistoryFromTrials(_trials));

            return;

            function getMillis(doc) {
              return doc.created ? doc.created.toMillis() : Date.now();
            }
          });
        unsubscribeExercises = firebase
          .firestore()
          .collection("exercises")
          .onSnapshot(snapshot => {
            setExercises(parseAll(snapshot));
          });
        unsubscribeProfile = firebase
          .firestore()
          .collection("users")
          .where("userId", "==", user.uid)
          .onSnapshot(({ docs }) => {
            if (docs.length === 1) {
              const p = parseDocSnapshot(docs[0]);
              if (p.username) {
                window.analytics.identify(user.uid, {
                  username: p.username,
                });
              }
              setProfile(p);
            }
          });
      } else {
        setUser(false);
        setProfile();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
      if (unsubscribeTrials) {
        unsubscribeTrials();
      }
      if (unsubscribeExercises) {
        unsubscribeExercises();
      }
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };

    function parseAll(snapshot) {
      const newDocs = [];
      snapshot.docs.forEach(docSnapshot => {
        newDocs.push(parseDocSnapshot(docSnapshot));
      });
      return newDocs;
    }
  }, []);

  const [avatarUrl, setAvatarUrl] = useState();
  useEffect(() => {
    if (profile) {
      getFile(
        profile && profile.avatarLocation
          ? profile.avatarLocation
          : "/user/default/default.png"
      ).then(setAvatarUrl);
    }
  }, [profile]);

  return {
    avatarUrl,
    confirmPasswordReset,
    error,
    exercises,
    glickoHistory,
    profile,
    reset,
    sendPasswordResetEmail,
    signin,
    signout,
    signup,
    trials,
    user
  };

  // Wrap any Firebase methods we want to use making sure
  // to save the user to state.
  function signin(email, password) {
    setError(null);
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(response => {
        setUser(response.user);
        return response.user;
      })
      .catch(error => {
        setError(error);
        return error;
      });
  }

  function signup(email, password) {
    setError(null);
    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(response => {
        setUser(response.user);
        return response.user;
      })
      .catch(error => {
        setError(error);
        return error;
      });
  }

  function signout() {
    return firebase
      .auth()
      .signOut()
      .then(() => {
        setUser(false);
      });
  }

  function reset() {
    setError(null);
  }

  function sendPasswordResetEmail(email) {
    return firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => true);
  }

  function confirmPasswordReset(code, password) {
    return firebase
      .auth()
      .confirmPasswordReset(code, password)
      .then(() => true);
  }
}
